import {
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Progress,
  Select,
  Table,
  Tag,
  Typography,
} from "antd";
import { type Key, useRef, useState } from "react";

import { ApiError } from "../api/client";
import { completeUpload, createUploadIntent, uploadToIntent } from "../api/attachments";
import { fetchAgentProfile, updateAgentProfile, type AgentProfile } from "../api/auth";
import {
  createRegistrant,
  deleteRegistrant,
  fetchCertificateNumber,
  fetchRegistrants,
  updateRegistrant,
  type CertificateType,
  type Registrant,
  type RegistrantCategoryFilter,
  type RegistrantType,
  type RegistrantWritePayload,
} from "../api/registrants";
import { fetchUsers } from "../api/users";
import { useAuth } from "../features/auth/AuthContext";
import { AttachmentPanel } from "../features/attachments/AttachmentPanel";

const TYPE_LABELS: Record<RegistrantType, string> = {
  NATURAL_PERSON: "自然人",
  LEGAL_ENTITY: "法人或其他组织",
  OTHER_ORGANIZATION: "法人或其他组织",
};
const CERTIFICATE_LABELS: Record<CertificateType, string> = {
  IDENTITY_CARD: "居民身份证",
  PASSPORT: "护照",
  UNIFIED_SOCIAL_CREDIT_CODE: "统一社会信用代码证件",
  OTHER: "其他证件",
};
const TYPE_FILTER_OPTIONS: Array<{ value: RegistrantCategoryFilter; label: string }> = [
  { value: "NATURAL_PERSON", label: "自然人" },
  { value: "ORGANIZATION", label: "法人或其他组织" },
];
const MAX_CERTIFICATE_FILE_SIZE = 5 * 1024 * 1024;
const MAX_CERTIFICATE_FILES = 2;

interface FormValues extends RegistrantWritePayload {
  certificate_number: string;
}

interface AgentFormValues {
  name: string;
  phone?: string;
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

function maskContact(value?: string | null) {
  const text = value?.trim();
  if (!text) return "未设置";
  if (/^\d{7,}$/.test(text)) return `${text.slice(0, 3)}****${text.slice(-4)}`;
  if (text.includes("@")) {
    const [name, domain] = text.split("@");
    return `${name.slice(0, 2)}****@${domain}`;
  }
  return text.length > 6 ? `${text.slice(0, 3)}****${text.slice(-2)}` : text;
}

export function RegistrantManagementPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();
  const [agentForm] = Form.useForm<AgentFormValues>();
  const certificateInputRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [registrantCategory, setRegistrantCategory] = useState<RegistrantCategoryFilter>();
  const [ownerUserId, setOwnerUserId] = useState<string>();
  const [editing, setEditing] = useState<Registrant | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [certificateRegistrant, setCertificateRegistrant] = useState<Registrant | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [certificateFiles, setCertificateFiles] = useState<File[]>([]);
  const [attachmentProgress, setAttachmentProgress] = useState(0);

  const query = useQuery({
    queryKey: ["registrants", page, pageSize, search, registrantCategory, ownerUserId],
    queryFn: ({ signal }) =>
      fetchRegistrants(
        {
          page,
          pageSize,
          search,
          ownerUserId,
          registrantCategory,
        },
        signal,
      ),
    refetchInterval: (result) =>
      result.state.data?.items.some((item) => item.has_pending_certificate) ? 1500 : false,
  });
  const ownersQuery = useQuery({
    queryKey: ["registrant-owner-options"],
    queryFn: ({ signal }) =>
      fetchUsers({ page: 1, pageSize: 100, role: "NORMAL_USER", status: "ACTIVE" }, signal),
    enabled: isAdmin,
    staleTime: 60_000,
  });

  const agentProfileQuery = useQuery({
    queryKey: ["agent-profile"],
    queryFn: ({ signal }) => fetchAgentProfile(signal),
    staleTime: 60_000,
  });

  const defaultAgentName = agentProfileQuery.data?.name ?? user?.name ?? "当前账号";
  const defaultAgentContact = maskContact(agentProfileQuery.data?.phone);
  const registrants = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const common = {
        owner_user_id: values.owner_user_id,
        registrant_type: values.registrant_type,
        name: values.name.trim(),
        certificate_type: values.certificate_type,
      };
      if (editing) {
        const registrant = await updateRegistrant(editing.id, {
          ...common,
          certificate_number: values.certificate_number?.trim() || undefined,
          version: editing.version,
        });
        return { registrant, attachmentStatus: "none" as const, attachmentError: null };
      }

      const registrant = await createRegistrant({
        ...common,
        certificate_number: values.certificate_number.trim(),
      });
      if (!certificateFiles.length) {
        return { registrant, attachmentStatus: "none" as const, attachmentError: null };
      }

      const uploadErrors: unknown[] = [];
      setAttachmentProgress(0);
      for (const [index, file] of certificateFiles.entries()) {
        try {
          const intent = await createUploadIntent({ registrantId: registrant.id }, file);
          await uploadToIntent(intent, file, (progress) =>
            setAttachmentProgress(Math.round((index * 100 + progress) / certificateFiles.length)),
          );
          await completeUpload(intent.attachment.id);
        } catch (error) {
          uploadErrors.push(error);
        }
      }
      const uploadedCount = certificateFiles.length - uploadErrors.length;
      return {
        registrant,
        attachmentStatus:
          uploadedCount === certificateFiles.length
            ? ("uploaded" as const)
            : uploadedCount > 0
              ? ("partial" as const)
              : ("failed" as const),
        attachmentError: uploadErrors[0] ?? null,
      };
    },
    onSuccess: async ({ attachmentStatus, attachmentError }) => {
      if (attachmentStatus === "failed" || attachmentStatus === "partial") {
        message.warning(
          attachmentStatus === "partial"
            ? "登记人已创建，部分附件上传失败，请在列表中重新上传"
            : `登记人已创建，但附件上传失败：${errorMessage(attachmentError, "请在列表中重新上传")}`,
        );
      } else {
        message.success(
          editing
            ? "登记人资料已更新"
            : attachmentStatus === "uploaded"
              ? "登记人及附件已保存"
              : "登记人已创建",
        );
      }
      setDrawerOpen(false);
      setCertificateFiles([]);
      setAttachmentProgress(0);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ["registrants"] });
    },
    onError: (error) => message.error(errorMessage(error, "保存失败")),
  });
  const saveAgentMutation = useMutation({
    mutationFn: (values: AgentFormValues) => {
      const profile = agentProfileQuery.data;
      if (!profile) throw new Error("经办人资料尚未加载");
      return updateAgentProfile({
        name: values.name.trim(),
        phone: values.phone?.trim() || null,
        version: profile.version,
      });
    },
    onSuccess: async (profile) => {
      queryClient.setQueryData<AgentProfile>(["agent-profile"], profile);
      setAgentModalOpen(false);
      message.success("登记经办人信息已保存");
      await queryClient.invalidateQueries({ queryKey: ["current-user"] });
    },
    onError: (error) => message.error(errorMessage(error, "登记经办人信息保存失败")),
  });

  const openAgentEditor = () => {
    const profile = agentProfileQuery.data;
    if (!profile) {
      void message.warning("经办人资料正在加载，请稍后重试");
      return;
    }
    agentForm.setFieldsValue({ name: profile.name, phone: profile.phone ?? "" });
    setAgentModalOpen(true);
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setCertificateFiles([]);
    setAttachmentProgress(0);
    form.setFieldsValue({
      registrant_type: "NATURAL_PERSON",
      certificate_type: "IDENTITY_CARD",
    });
    setDrawerOpen(true);
  };
  const openEdit = (record: Registrant) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      owner_user_id: record.owner_user_id,
      registrant_type:
        record.registrant_type === "OTHER_ORGANIZATION" ? "LEGAL_ENTITY" : record.registrant_type,
      name: record.name,
      certificate_type: record.certificate_type,
      certificate_number: "",
    });
    setDrawerOpen(true);
  };
  const chooseCertificateFiles = (files: File[]) => {
    const validFiles = files.filter((file) => {
      const extension = file.name.includes(".") ? file.name.split(".").pop()?.toLowerCase() : "";
      if (!extension || !["png", "jpg"].includes(extension)) {
        void message.error(`${file.name}：仅支持 PNG、JPG 图片格式`);
        return false;
      }
      if (file.size > MAX_CERTIFICATE_FILE_SIZE) {
        void message.error(`${file.name}：图片不能超过 5 MB`);
        return false;
      }
      return true;
    });
    setCertificateFiles((current) => {
      const existing = new Set(
        current.map((file) => `${file.name}-${file.size}-${file.lastModified}`),
      );
      const uniqueFiles = validFiles.filter((file) => {
        const signature = `${file.name}-${file.size}-${file.lastModified}`;
        if (existing.has(signature)) return false;
        existing.add(signature);
        return true;
      });
      const available = MAX_CERTIFICATE_FILES - current.length;
      if (uniqueFiles.length > available) {
        void message.warning("每个登记人最多上传 2 张图片");
      }
      return [...current, ...uniqueFiles.slice(0, available)];
    });
    setAttachmentProgress(0);
  };
  const revealNumber = async (record: Registrant) => {
    try {
      const result = await fetchCertificateNumber(record.id);
      Modal.info({
        title: `${record.name} 的证件号码`,
        content: (
          <div className="sensitive-number">
            <Typography.Text type="secondary">
              {CERTIFICATE_LABELS[result.certificate_type]}
            </Typography.Text>
            <Typography.Text copyable code>
              {result.certificate_number}
            </Typography.Text>
            <Typography.Text type="warning">本次敏感信息查看已写入审计日志。</Typography.Text>
          </div>
        ),
        okText: "关闭",
      });
    } catch (error) {
      message.error(errorMessage(error, "证件号码读取失败"));
    }
  };
  const remove = async (record: Registrant) => {
    try {
      await deleteRegistrant(record.id);
      message.success("登记人已删除，历史项目关系仍保留");
      await queryClient.invalidateQueries({ queryKey: ["registrants"] });
    } catch (error) {
      message.error(errorMessage(error, "删除失败"));
    }
  };
  const applySearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
    setSelectedRowKeys([]);
  };
  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setRegistrantCategory(undefined);
    setOwnerUserId(undefined);
    setPage(1);
    setSelectedRowKeys([]);
  };
  const tableColumns = [
    {
      title: "序号",
      width: 72,
      render: (_: unknown, __: Registrant, index: number) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "登记人名称",
      dataIndex: "name" as const,
      width: 260,
    },
    {
      title: "类别",
      dataIndex: "registrant_type" as const,
      width: 170,
      render: (value: RegistrantType) => (
        <Tag
          className={
            value === "NATURAL_PERSON" ? "registrant-type-tag is-person" : "registrant-type-tag"
          }
        >
          {TYPE_LABELS[value]}
        </Tag>
      ),
    },
    {
      title: "证件号码",
      key: "certificate",
      width: 330,
      render: (_: unknown, record: Registrant) => (
        <span className="registrant-certificate-cell">
          {record.certificate_number_masked}
          {!record.is_deleted ? (
            <Button
              aria-label="查看证件号码"
              type="text"
              size="small"
              icon={<EyeInvisibleOutlined />}
              onClick={() => void revealNumber(record)}
            />
          ) : null}
        </span>
      ),
    },
    ...(isAdmin
      ? [
          {
            title: "所属用户",
            dataIndex: "owner_name" as const,
            width: 150,
          },
        ]
      : []),
    {
      title: "附件",
      key: "attachment",
      width: 160,
      render: (_: unknown, record: Registrant) => {
        if (record.has_active_certificate) {
          return (
            <Button
              className="registrant-outline-button"
              icon={<EyeOutlined />}
              onClick={() => setCertificateRegistrant(record)}
            >
              查看
            </Button>
          );
        }
        if (record.has_pending_certificate) {
          return (
            <Button
              className="registrant-outline-button"
              icon={<LoadingOutlined spin />}
              onClick={() => setCertificateRegistrant(record)}
            >
              校验中
            </Button>
          );
        }
        return (
          <Button
            className="registrant-outline-button"
            icon={<UploadOutlined />}
            onClick={() => setCertificateRegistrant(record)}
          >
            上传
          </Button>
        );
      },
    },
    {
      title: "操作",
      key: "actions",
      fixed: "right" as const,
      width: 210,
      render: (_: unknown, record: Registrant) =>
        record.is_deleted ? (
          <Typography.Text type="secondary">已删除</Typography.Text>
        ) : (
          <div className="registrant-row-actions">
            <Button type="link" onClick={() => openEdit(record)}>
              编辑
            </Button>
            <Popconfirm
              title="删除登记人？"
              description="删除后不能用于新项目，历史项目不受影响。"
              okText="删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
              onConfirm={() => void remove(record)}
            >
              <Button danger type="link">
                删除
              </Button>
            </Popconfirm>
          </div>
        ),
    },
  ];

  return (
    <main className="management-page registrant-page">
      <div className="page-heading registrant-page-heading">
        <div>
          <Typography.Title level={1}>登记人信息</Typography.Title>
          <Typography.Paragraph type="secondary">
            统一维护登记主体与账号经办人信息，创建项目时可直接选择
          </Typography.Paragraph>
        </div>
        <Button
          className="registrant-primary-button"
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
        >
          新增登记人
        </Button>
      </div>

      <section className="registrant-agent-panel" aria-label="登记经办人">
        <div className="registrant-agent-avatar">
          <UserOutlined />
        </div>
        <div className="registrant-agent-copy">
          <div>
            <strong>登记经办人</strong>
            <Tag>账号共用</Tag>
          </div>
          <p>当前账号创建的所有登记项目默认使用以下登记经办人信息</p>
        </div>
        <div className="registrant-agent-field">
          <span>经办人姓名</span>
          <strong>{defaultAgentName}</strong>
        </div>
        <div className="registrant-agent-field">
          <span>手机号码</span>
          <strong>{defaultAgentContact}</strong>
        </div>
        <div className="registrant-agent-action">
          <Button
            className="registrant-outline-button"
            icon={<EditOutlined />}
            loading={agentProfileQuery.isPending}
            onClick={openAgentEditor}
          >
            编辑登记经办人
          </Button>
          <small>修改后将作为后续项目的默认登记经办人</small>
        </div>
      </section>

      <section className="registrant-list-panel" aria-label="登记人列表">
        <header className="registrant-list-header">
          <div>
            <strong>登记人列表</strong>
            <span>共 {total} 个登记人</span>
          </div>
          <p>登记人可在项目中作为著作权人或作者使用</p>
        </header>

        <div className="registrant-filter-row">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="搜索登记人名称或证件号码"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onPressEnter={applySearch}
          />
          <Select<RegistrantCategoryFilter>
            allowClear
            placeholder="登记人类别"
            value={registrantCategory}
            options={TYPE_FILTER_OPTIONS}
            onChange={(value) => {
              setRegistrantCategory(value);
              setPage(1);
            }}
          />
          {isAdmin ? (
            <Select<string>
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="所属用户"
              loading={ownersQuery.isPending}
              value={ownerUserId}
              options={ownersQuery.data?.items.map((item) => ({
                value: item.id,
                label: `${item.name}（${item.username}）`,
              }))}
              onChange={(value) => {
                setOwnerUserId(value);
                setPage(1);
              }}
            />
          ) : null}
          <Button onClick={resetFilters}>重置</Button>
          <Button type="primary" onClick={applySearch}>
            查询
          </Button>
        </div>

        <Table<Registrant>
          className="registrant-table"
          rowKey="id"
          loading={query.isPending}
          dataSource={registrants}
          columns={tableColumns}
          pagination={false}
          scroll={{ x: isAdmin ? 1260 : 1100 }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
        />

        <div className="registrant-table-note">
          <InfoCircleOutlined />
          证件附件仅可通过「查看」按钮访问，系统将记录操作日志
        </div>
      </section>

      <footer className="registrant-footer">
        <div className="registrant-footer-note">
          <InfoCircleOutlined />
          登记人资料由当前账号统一维护，创建项目时可直接选择，无需重复录入
        </div>
        <div className="registrant-pagination">
          <span>共 {total} 条</span>
          <Select
            value={pageSize}
            options={[
              { value: 10, label: "10条/页" },
              { value: 20, label: "20条/页" },
              { value: 50, label: "50条/页" },
            ]}
            onChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
          />
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger={false}
            onChange={(nextPage) => {
              setPage(nextPage);
              setSelectedRowKeys([]);
            }}
          />
        </div>
      </footer>

      <Modal
        className="registrant-agent-modal"
        title="编辑登记经办人"
        open={agentModalOpen}
        okText="保存"
        cancelText="取消"
        confirmLoading={saveAgentMutation.isPending}
        destroyOnHidden
        onOk={() => agentForm.submit()}
        onCancel={() => setAgentModalOpen(false)}
      >
        <p className="registrant-agent-modal-tip">修改后将作为后续项目的默认登记经办人信息</p>
        <Form<AgentFormValues>
          form={agentForm}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => saveAgentMutation.mutate(values)}
        >
          <Form.Item
            name="name"
            label="经办人姓名"
            rules={[
              { required: true, message: "请输入经办人姓名" },
              { max: 100, message: "经办人姓名不能超过 100 个字符" },
            ]}
          >
            <Input autoComplete="name" placeholder="请输入经办人姓名" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="手机号码"
            rules={[
              { max: 32, message: "手机号码不能超过 32 个字符" },
              {
                pattern: /^[0-9 +\-()]*$/,
                message: "手机号码只能包含数字、空格和 + - ( )",
              },
            ]}
          >
            <Input autoComplete="tel" inputMode="tel" placeholder="请输入手机号码" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={editing ? "编辑登记人" : "新增登记人"}
        size={520}
        open={drawerOpen}
        destroyOnHidden
        onClose={() => {
          setDrawerOpen(false);
          setCertificateFiles([]);
          setAttachmentProgress(0);
        }}
        extra={
          <Button type="primary" loading={saveMutation.isPending} onClick={() => form.submit()}>
            保存
          </Button>
        }
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => saveMutation.mutate(values)}
        >
          {isAdmin && !editing ? (
            <Form.Item name="owner_user_id" label="所属用户" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="选择普通用户"
                options={ownersQuery.data?.items.map((item) => ({
                  value: item.id,
                  label: `${item.name}（${item.username}）`,
                }))}
              />
            </Form.Item>
          ) : null}
          {isAdmin && editing ? (
            <Form.Item label="所属用户">
              <Input value={editing.owner_name} disabled />
            </Form.Item>
          ) : null}
          <Form.Item name="registrant_type" label="登记人类别" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "NATURAL_PERSON", label: "自然人" },
                { value: "LEGAL_ENTITY", label: "法人或其他组织" },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="登记人名称"
            rules={[
              { required: true, message: "请输入登记人名称" },
              { max: 50, message: "登记人名称不能超过 50 个字" },
            ]}
          >
            <Input showCount maxLength={50} placeholder="请输入真实主体名称" />
          </Form.Item>
          <Form.Item name="certificate_type" label="证件类型" rules={[{ required: true }]}>
            <Select
              options={Object.entries(CERTIFICATE_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="certificate_number"
            label="证件号码"
            extra={
              editing
                ? `留空则保持原号码（当前 ${editing.certificate_number_masked}）`
                : "保存后默认脱敏展示"
            }
            rules={
              editing
                ? [{ max: 50, message: "证件号码不能超过 50 个字" }]
                : [
                    { required: true, message: "请输入证件号码" },
                    { max: 50, message: "证件号码不能超过 50 个字" },
                  ]
            }
          >
            <Input
              autoComplete="off"
              showCount
              maxLength={50}
              placeholder={editing ? "留空不修改" : "请输入证件号码"}
            />
          </Form.Item>
          {!editing ? (
            <Form.Item label="证件附件" extra="支持 PNG、JPG 图片，单张不超过 5 MB，最多上传 2 张">
              <input
                ref={certificateInputRef}
                className="visually-hidden"
                type="file"
                accept=".png,.jpg"
                multiple
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  if (files.length) chooseCertificateFiles(files);
                  event.target.value = "";
                }}
              />
              {certificateFiles.length ? (
                <div className="registrant-certificate-upload-list">
                  {certificateFiles.map((file, index) => (
                    <div
                      className="registrant-certificate-upload is-selected"
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                    >
                      <div>
                        <UploadOutlined />
                        <span>
                          <strong>{file.name}</strong>
                          <small>{(file.size / 1024 / 1024).toFixed(2)} MB</small>
                        </span>
                      </div>
                      <Button
                        type="text"
                        danger
                        aria-label={`移除证件附件 ${file.name}`}
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          setCertificateFiles((current) =>
                            current.filter((_, fileIndex) => fileIndex !== index),
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : null}
              {certificateFiles.length < MAX_CERTIFICATE_FILES ? (
                <button
                  className="registrant-certificate-upload"
                  type="button"
                  onClick={() => certificateInputRef.current?.click()}
                >
                  <UploadOutlined />
                  <span>
                    <strong>{certificateFiles.length ? "继续添加图片" : "选择证件图片"}</strong>
                    <small>还可添加 {MAX_CERTIFICATE_FILES - certificateFiles.length} 张</small>
                  </span>
                </button>
              ) : null}
              {saveMutation.isPending && certificateFiles.length ? (
                <Progress percent={attachmentProgress} size="small" status="active" />
              ) : null}
            </Form.Item>
          ) : null}
        </Form>
      </Drawer>

      <Drawer
        title={certificateRegistrant ? `${certificateRegistrant.name} · 证件附件` : "证件附件"}
        size={720}
        open={Boolean(certificateRegistrant)}
        destroyOnHidden
        onClose={() => {
          setCertificateRegistrant(null);
          void query.refetch();
        }}
      >
        {certificateRegistrant ? (
          <AttachmentPanel registrantId={certificateRegistrant.id} canUpload />
        ) : null}
      </Drawer>
    </main>
  );
}
